import React, { SyntheticEvent, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { FC } from 'react';
import { SaveAndStartJoinCampaignButton } from 'src/modules/campaigns/components/SaveAndStartJoinCampaignButton';
import { Group } from 'src/modules/groups/types';
import SelectWithSearch from 'src/components/SelectWithSearch/view';
import { formatGroupContactCountSuffix } from 'src/components/GroupOptionLabel';

type ContactGroupsFormProps = {
  currentCompanyId: string;
  contactId: string;
  contactGroupIds: string[];
  setIsFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  isFormDirty: boolean;
  groups: Group[];
  submitContactGroups: (
    companyId: string,
    contactId: string,
    newGroupIds: string[],
    removedGroupIds: string[],
  ) => Promise<boolean>;
};

export const getGroupSelectionChanges = (
  contactGroupIds: string[],
  selectedGroupIds: string[],
  selectableGroupIds: Set<string>,
) => ({
  newGroupIds: selectedGroupIds.filter(id => !contactGroupIds.includes(id)),
  removedGroupIds: contactGroupIds.filter(
    id => selectableGroupIds.has(id) && !selectedGroupIds.includes(id),
  ),
});

export const EditContactGroupsForm: FC<ContactGroupsFormProps> = props => {
  const {
    currentCompanyId,
    contactId,
    contactGroupIds,
    groups,
    isFormDirty,
    setIsFormDirty,
    submitContactGroups,
  } = props;
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(contactGroupIds);
  const [removedGroupIds, setRemovedGroupIds] = useState<string[]>([]);
  const [newGroupIds, setNewGroupIds] = useState<string[]>([]);

  const submit = async () => {
    const saved = await submitContactGroups(currentCompanyId, contactId, newGroupIds, removedGroupIds);
    if (!saved) return undefined;
    setIsFormDirty(false);
    return [contactId];
  };

  const selectableGroups = useMemo(
    () =>
      groups
        .filter(g => g.type !== 'default')
        .sort((a, b) => a.type.toLowerCase().localeCompare(b.type.toLowerCase())),
    [groups],
  );

  const selectableGroupIds = useMemo(
    () => new Set(selectableGroups.map(g => g.id)),
    [selectableGroups],
  );

  const groupOptions = useMemo(
    () =>
      selectableGroups.map(({ name, id, status, type, validContactCount, joinCampaigns }) => ({
        text: name || '',
        secondaryText: formatGroupContactCountSuffix(validContactCount, joinCampaigns?.length),
        value: id,
        isArchived: status === 'archived',
        isDisabled: type === 'dynamic',
      })),
    [selectableGroups],
  );

  const newGroupsHaveJoinCampaigns = groups.some(
    g => newGroupIds.includes(g.id) && g.joinCampaigns?.length > 0,
  );

  const handleSelectedGroupsChange = (
    _: SyntheticEvent<Element, Event> | undefined,
    items: { text: string; value: string }[],
  ) => {
    const selectedGroupIds = items?.map(({ value }) => value) || [];
    const { newGroupIds, removedGroupIds } = getGroupSelectionChanges(
      contactGroupIds,
      selectedGroupIds,
      selectableGroupIds,
    );
    setIsFormDirty(true);
    setSelectedGroupIds(selectedGroupIds);
    setNewGroupIds(newGroupIds);
    setRemovedGroupIds(removedGroupIds);
  };

  return (
    <>
      <Grid>
        {groups && (
          <SelectWithSearch
            disableClearable={true}
            showResultsUnderSearchTextBox
            hideStatusIcon
            label="Groups"
            placeholder="Groups"
            style={{
              width: '100%',
              margin: '0 0 20px 0',
              maxHeight: '250px',
              paddingTop: '10px',
              overflow: 'auto',
            }}
            options={groupOptions}
            values={selectedGroupIds}
            showArchived={true}
            handleSelectChange={(e, items) => handleSelectedGroupsChange(e, items)}
          />
        )}
      </Grid>
      <Grid container direction={'row-reverse'}>
        <SaveAndStartJoinCampaignButton
          label="Update"
          disabled={!isFormDirty}
          color="secondary"
          size="large"
          // Offered only when a group the contact is joining has join campaigns; the default group's are offered with them.
          groupIds={newGroupIds}
          includeDefaultGroup
          hideStartOption={!newGroupsHaveJoinCampaigns}
          onSave={submit}
        />
      </Grid>
    </>
  );
};
